import type { ReactNode } from 'react';
import {
  AreaCrumbs,
  AreaSwitcher,
  useAreaHeader,
  useRegisterAreaHeader,
} from '@/shared/navigation';
import { cn } from '@/shared/lib/cn';
import type { HeroBadge, HeroTone } from './bookingHeroModel';
import { BookingHeroSteps } from './BookingHeroSteps';
import type { ProgressStep } from './progressModel';

interface Props {
  title: string;
  badges: HeroBadge[];
  meta: string;
  source: { letter: string; label: string; bgClass: string; textClass: string };
  /** „Daten verwaltet in Booking.com …“ – nur bei Kanalbuchungen. */
  sourceNote: string | null;
  steps: ProgressStep[];
  stepSubs: Record<string, string>;
  /** Reiter der Buchung; ohne sie (Eigentümer) endet der Kopf mit den Schritten. */
  tabs?: ReactNode;
}

const BADGE: Record<HeroTone, string> = {
  green: 'bg-mint text-forest-deep',
  amber: 'bg-amber-tint text-amber-ink',
  red: 'bg-sealwax-tint text-sealwax',
  grey: 'bg-white/15 text-white',
};

/**
 * Wald-Kopf der Buchung (Board „Buchung“): Pfad bis zum Gast, Bereichsumschalter, Titel mit
 * Status und Phase, eine Meta-Zeile, Quelle, die vier Schritte und die Reiter.
 */
export function BookingHero({
  title,
  badges,
  meta,
  source,
  sourceNote,
  steps,
  stepSubs,
  tabs,
}: Props) {
  useRegisterAreaHeader();
  const area = useAreaHeader();
  const crumbs = [
    ...area.crumbs.map((c) => ({ ...c, current: false })),
    { label: title, to: '', current: true },
  ];
  return (
    <header className="on-forest bg-forest-deep rounded-hero relative mb-5 flex-none overflow-hidden px-5 py-5 text-white md:px-8 md:py-6">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(115deg,rgba(255,255,255,0.035)_0_2px,transparent_2px_24px)]"
      />
      <div className="relative flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <AreaCrumbs crumbs={crumbs} />
          <AreaSwitcher items={area.switcher} />
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="font-serif text-3xl font-bold tracking-[-0.03em] md:text-[40px] md:leading-none">
                {title}
              </h1>
              {badges.map((badge) => (
                <span
                  key={badge.label}
                  className={cn(
                    'rounded-pill inline-flex h-7 items-center px-3 text-xs font-bold',
                    BADGE[badge.tone],
                  )}
                >
                  {badge.label}
                </span>
              ))}
            </div>
            <p className="text-on-forest-soft text-sm">{meta}</p>
          </div>
          <div className="flex flex-col items-start gap-1.5 xl:items-end">
            <span className="rounded-pill flex h-8 items-center gap-2 bg-white/10 px-3 text-[13px] font-semibold">
              <span
                aria-hidden
                className={cn(
                  'flex size-[22px] items-center justify-center rounded-md text-[11px] font-extrabold',
                  source.bgClass,
                  source.textClass,
                )}
              >
                {source.letter}
              </span>
              {source.label}
            </span>
            {sourceNote && <span className="text-on-forest-soft text-xs">{sourceNote}</span>}
          </div>
        </div>
        <BookingHeroSteps steps={steps} stepSubs={stepSubs} />
        {tabs}
      </div>
    </header>
  );
}
